import { useState, useMemo } from "react";
import KpiCard, { KpiRow } from "@/components/KpiCard";
import InfoTooltip from "@/components/InfoTooltip";
import ChartFrame from "@/components/ChartFrame";
import ToggleGroup from "@/components/ToggleGroup";
import { endpointDot } from "@/lib/chartStyle";
import { fmt, toolLabel } from "@/lib/format";
import {
  ComposedChart, Bar, Line, Legend, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, ReferenceLine,
} from "recharts";
import Panel from "../Panel";
import { allDates } from "./lib";

// ──────────────────────────────────────────────────────────────────────────────
// CacheSegment — FinOps 캐시 효율 (2026-08-21 redesign)
//
// 구조:
//  1. KPI 3개 (기간 히트율 / 절감액 / Write Tokens)
//  2. 상태 배너 — baseline 대비 ≥15%p 하락 감지
//  3. Trend chart — daily hit rate line + token usage bars + baseline dashed line
//  4. Daily Detail Table — date/input/read/write/hitRate/savings/drop tag
//  5. Tool/Model Grid — heatmap 형태, average column, usage 기준 정렬
//
// 데이터: 모두 Backend FinOps API canonical 값 재사용. 프론트 재계산 없음.
// ──────────────────────────────────────────────────────────────────────────────

const CACHE_DIMS = [
  { id: "all", label: "전체" },
  { id: "model", label: "모델별" },
  { id: "tool", label: "도구별" },
];
const LINE_COLORS = [
  "#2563eb", "#10b981", "#f59e0b", "#8b5cf6", "#f43f5e", "#0ea5e9",
  "#84cc16", "#d946ef", "#f97316", "#14b8a6", "#6366f1", "#e11d48",
  "#a16207", "#64748b",
];

const BASELINE_DROP_THRESHOLD = 15; // %p

function CacheSegment({ d, range }) {
  const [dim, setDim] = useState("all");
  const [hidden, setHidden] = useState(() => new Set());
  const [gridDim, setGridDim] = useState("tool");
  const cache = d.cache;

  const axis = allDates(range.from, range.to);

  // ── Baseline 계산 — 기간 전체 평균 hitRatePct ──
  const seriesMap = useMemo(() =>
    Object.fromEntries((d.cacheSeries || []).map((r) => [r.date, r])),
    [d.cacheSeries]);

  const validRates = useMemo(() =>
    (d.cacheSeries || []).filter((r) => r.hitRatePct != null).map((r) => r.hitRatePct),
    [d.cacheSeries]);

  const baseline = useMemo(() =>
    validRates.length ? Math.round(validRates.reduce((a, b) => a + b, 0) / validRates.length * 10) / 10 : null,
    [validRates]);

  // ── 상태 배너 — 최근 3일 평균 vs baseline ──
  const recentDrop = useMemo(() => {
    if (baseline == null || validRates.length < 4) return null;
    const sorted = (d.cacheSeries || [])
      .filter((r) => r.hitRatePct != null)
      .sort((a, b) => (a.date > b.date ? -1 : 1));
    const recent3 = sorted.slice(0, 3);
    if (recent3.length < 2) return null;
    const recentAvg = recent3.reduce((a, r) => a + r.hitRatePct, 0) / recent3.length;
    const drop = baseline - recentAvg;
    return drop >= BASELINE_DROP_THRESHOLD ? Math.round(drop * 10) / 10 : null;
  }, [d.cacheSeries, baseline, validRates.length]);

  // ── 일별 토큰 볼륨 맵 ──
  const volByDate = useMemo(() =>
    Object.fromEntries((d.cacheSeries || []).map((r) =>
      [r.date, (r.inputTokens || 0) + (r.readTokens || 0) + (r.writeTokens || 0)])),
    [d.cacheSeries]);

  // ── Trend chart 데이터 ──
  const { data: trendData, lines } = useMemo(() => {
    let data = [];
    let lines = [];
    if (dim === "all") {
      data = axis.map((date) => ({
        date,
        hitRatePct: seriesMap[date]?.hitRatePct ?? null,
        tokens: volByDate[date] ?? 0,
      }));
      lines = [{ key: "hitRatePct", name: "캐시 히트율" }];
    } else {
      const groups = (dim === "model" ? d.cacheSeriesByModel : d.cacheSeriesByTool) || [];
      const byDate = {};
      groups.forEach((g) => (g.series || []).forEach((pt) => {
        (byDate[pt.date] = byDate[pt.date] || {})[g.key] = pt.hitRatePct;
      }));
      data = axis.map((date) => ({ date, tokens: volByDate[date] ?? 0, ...(byDate[date] || {}) }));
      lines = groups.map((g) => ({ key: g.key, name: dim === "tool" ? toolLabel(g.key) : g.key }));
    }
    return { data, lines };
  }, [dim, axis, seriesMap, volByDate, d.cacheSeriesByModel, d.cacheSeriesByTool]);

  // ── Daily Detail Table 데이터 ──
  const dailyRows = useMemo(() =>
    axis.map((date) => {
      const r = seriesMap[date];
      const hitRate = r?.hitRatePct ?? null;
      const drop = baseline != null && hitRate != null && (baseline - hitRate) >= BASELINE_DROP_THRESHOLD;
      return {
        date,
        inputTokens: r?.inputTokens ?? 0,
        readTokens: r?.readTokens ?? 0,
        writeTokens: r?.writeTokens ?? 0,
        hitRatePct: hitRate,
        savedKrw: r?.savedKrw ?? 0,
        drop,
      };
    }).sort((a, b) => (a.date > b.date ? -1 : 1)),
    [axis, seriesMap, baseline]);

  // ── Tool/Model Grid 데이터 ──
  const gridData = useMemo(() => {
    const groups = (gridDim === "model" ? d.cacheSeriesByModel : d.cacheSeriesByTool) || [];
    return groups.map((g) => {
      const byDate = Object.fromEntries((g.series || []).map((pt) => [pt.date, pt.hitRatePct]));
      const rates = (g.series || []).filter((pt) => pt.hitRatePct != null).map((pt) => pt.hitRatePct);
      const avg = rates.length ? Math.round(rates.reduce((a, b) => a + b, 0) / rates.length * 10) / 10 : null;
      return { key: g.key, byDate, avg, volume: rates.length };
    }).sort((a, b) => b.volume - a.volume);
  }, [gridDim, d.cacheSeriesByModel, d.cacheSeriesByTool]);

  const toggleLine = (key) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <>
      {/* ── 1. KPI 3개 ── */}
      <KpiRow cols={3}>
        <KpiCard label="기간 캐시 히트율" value={cache?.hitRatePct != null ? `${cache.hitRatePct}%` : "—"}
                 tone="brand" hero
                 sublabel="캐시 읽기 ÷ (입력+읽기+쓰기) · 높을수록 반복 입력을 싸게 처리" />
        <KpiCard label="기간 절감액" value={fmt.krw(cache?.savedKrw ?? 0)} tone="good"
                 sublabel="캐시 읽기가 정가 입력이었다면 냈을 금액의 90%" />
        <KpiCard label="캐시 쓰기 토큰" value={fmt.compact(cache?.writeTokens ?? 0)}
                 sublabel={`읽기 ${fmt.compact(cache?.readTokens ?? 0)} · 쓰기는 등록비(1.25배 단가)`} />
      </KpiRow>

      {/* ── 2. 상태 배너 ── */}
      <StatusBanner baseline={baseline} drop={recentDrop} />

      {/* ── 3. Trend Chart ── */}
      <Panel title="일별 캐시 히트율"
             action={
               <div className="flex items-center gap-2">
                 <span className="text-[11.5px] text-ink-400">범례 클릭 = 라인 숨김/표시</span>
                 <ToggleGroup options={CACHE_DIMS} value={dim} onChange={(v) => { setDim(v); setHidden(new Set()); }} />
               </div>
             }>
        <ChartFrame base="h-48 sm:h-64" tall="h-80 sm:h-[30rem]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={trendData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f2f2f0" />
              <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} minTickGap={28}
                     tick={{ fill: "#9a9a96", fontSize: 11 }} axisLine={false} tickLine={false} tickMargin={10} />
              <YAxis yAxisId="rate" domain={[0, 100]} tick={{ fill: "#9a9a96", fontSize: 11 }}
                     tickFormatter={(v) => `${v}%`} width={44} axisLine={false} tickLine={false} />
              <YAxis yAxisId="vol" orientation="right" tick={{ fill: "#c6c6c2", fontSize: 10 }}
                     tickFormatter={fmt.compact} width={44} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 14, border: "none", boxShadow: "0 6px 20px rgba(15,23,42,0.14)", fontSize: 12, padding: "8px 12px" }}
                formatter={(v, name) => name === "토큰 사용량"
                  ? [`${fmt.compact(v)} 토큰`, name]
                  : [v != null ? `${v}%` : "—", name]} />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 8, cursor: "pointer" }}
                onClick={(e) => e.dataKey !== "tokens" && toggleLine(e.dataKey)}
                formatter={(value, entry) => (
                  <span style={{ opacity: hidden.has(entry.dataKey) ? 0.4 : 1, textDecoration: hidden.has(entry.dataKey) ? "line-through" : "none" }}>
                    {value}
                  </span>
                )} />
              <Bar yAxisId="vol" dataKey="tokens" name="토큰 사용량" fill="#e2e8f0" radius={[3, 3, 0, 0]} />
              {dim === "all" && baseline != null && (
                <ReferenceLine yAxisId="rate" y={baseline} stroke="#94a3b8" strokeDasharray="6 4" strokeWidth={1.5}
                               label={{ value: `평균 ${baseline}%`, position: "right", fill: "#64748b", fontSize: 11 }} />
              )}
              {lines.map((l, i) => (
                <Line key={l.key} yAxisId="rate" type="natural" dataKey={l.key} name={l.name} connectNulls
                      stroke={LINE_COLORS[i % LINE_COLORS.length]} strokeWidth={i === 0 ? 3 : 2}
                      dot={endpointDot(LINE_COLORS[i % LINE_COLORS.length], trendData.length)} activeDot={{ r: 4 }}
                      hide={hidden.has(l.key)} />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </ChartFrame>
        <p className="mt-2 text-[12px] text-ink-400">
          회색 막대 = 그날 토큰 사용량 — 막대가 낮은 날의 히트율 등락은 노이즈입니다.
          {baseline != null && ` 점선 = 기간 평균(${baseline}%).`}
          {" "}히트율이 낮은 날은 새 대화(캐시 등록)가 많았던 날일 수 있어 나쁜 신호가 아니고, 사용량이 많은데 추세가 꺾이면 캐시 배선 회귀를 의심하세요.
        </p>
      </Panel>

      {/* ── 4. Daily Detail Table ── */}
      <Panel title="일별 캐시 상세"
             action={<span className="text-[11.5px] text-ink-400">{dailyRows.length}일 · 최신순</span>}>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="border-b border-ink-100 text-left text-ink-500">
                <th className="pb-2 pr-3 font-medium">날짜</th>
                <th className="pb-2 pr-3 font-medium text-right">Input</th>
                <th className="pb-2 pr-3 font-medium text-right">Cache Read</th>
                <th className="pb-2 pr-3 font-medium text-right">Cache Write</th>
                <th className="pb-2 pr-3 font-medium text-right">Hit Rate</th>
                <th className="pb-2 pr-3 font-medium text-right">절감액</th>
                <th className="pb-2 font-medium text-center">상태</th>
              </tr>
            </thead>
            <tbody>
              {dailyRows.map((row) => (
                <tr key={row.date} className="border-b border-ink-50 hover:bg-ink-50/50">
                  <td className="py-1.5 pr-3 font-mono text-ink-700">{row.date.slice(5)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-ink-600">{fmt.compact(row.inputTokens)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-ink-600">{fmt.compact(row.readTokens)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-ink-600">{fmt.compact(row.writeTokens)}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums font-medium text-ink-800">
                    {row.hitRatePct != null ? `${row.hitRatePct}%` : "—"}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-emerald-600">{fmt.krw(row.savedKrw)}</td>
                  <td className="py-1.5 text-center">
                    {row.drop
                      ? <span className="inline-block rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">▼ 하락</span>
                      : row.hitRatePct != null
                        ? <span className="inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-600">정상</span>
                        : <span className="text-ink-300">—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {/* ── 5. Tool/Model Grid (Heatmap) ── */}
      <Panel title="도구 · 모델별 캐시 효율"
             action={
               <ToggleGroup
                 options={[{ id: "tool", label: "도구별" }, { id: "model", label: "모델별" }]}
                 value={gridDim}
                 onChange={setGridDim} />
             }>
        {gridData.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-400">데이터 없음</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11.5px]">
              <thead>
                <tr className="border-b border-ink-100 text-left text-ink-500">
                  <th className="sticky left-0 bg-white pb-2 pr-3 font-medium">
                    {gridDim === "tool" ? "도구" : "모델"}
                  </th>
                  {axis.map((date) => (
                    <th key={date} className="pb-2 px-1 text-center font-mono font-normal">{date.slice(8)}</th>
                  ))}
                  <th className="pb-2 px-2 text-center font-medium">평균</th>
                </tr>
              </thead>
              <tbody>
                {gridData.map((row) => (
                  <tr key={row.key} className="border-b border-ink-50">
                    <td className="sticky left-0 bg-white py-1 pr-3 font-medium text-ink-700 truncate max-w-[120px]">
                      {gridDim === "tool" ? toolLabel(row.key) : row.key}
                    </td>
                    {axis.map((date) => {
                      const val = row.byDate[date];
                      return (
                        <td key={date} className="px-1 py-1 text-center">
                          <HeatCell value={val} baseline={baseline} />
                        </td>
                      );
                    })}
                    <td className="px-2 py-1 text-center font-medium text-ink-800">
                      {row.avg != null ? `${row.avg}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[11.5px] text-ink-400">
          셀 색: 초록 = 높은 히트율 / 빨강 = 기간 평균 대비 ≥15%p 하락 / 회색 = 데이터 없음. usage 기준 정렬.
        </p>
      </Panel>
    </>
  );
}

// ── 상태 배너 ──
function StatusBanner({ baseline, drop }) {
  if (baseline == null) return null;
  const isAlert = drop != null;
  return (
    <div className={`mb-4 flex items-center gap-3 rounded-xl border px-4 py-3 ${
      isAlert
        ? "border-red-200 bg-red-50/60"
        : "border-emerald-200 bg-emerald-50/60"
    }`}>
      <span className={`flex h-8 w-8 items-center justify-center rounded-full text-lg ${
        isAlert ? "bg-red-100" : "bg-emerald-100"
      }`}>
        {isAlert ? "⚠️" : "✓"}
      </span>
      <div>
        <p className={`text-sm font-semibold ${isAlert ? "text-red-800" : "text-emerald-800"}`}>
          {isAlert
            ? `캐시 히트율 주의 — 최근 3일이 기간 평균(${baseline}%) 대비 ${drop}%p 하락`
            : "캐시 효율 정상"
          }
        </p>
        <p className="text-[12px] text-ink-500">
          {isAlert
            ? "캐시 배선 회귀·모델 변경·새 도구 도입 시 일시 하락할 수 있습니다. 지속되면 원인 조사 필요."
            : `기간 평균 히트율 ${baseline}% — Baseline 대비 ≥15%p 하락 시 주의 배너가 나타납니다.`
          }
        </p>
      </div>
    </div>
  );
}

// ── Heatmap 셀 ──
function HeatCell({ value, baseline }) {
  if (value == null) {
    return <span className="inline-block h-5 w-5 rounded bg-ink-100" title="데이터 없음" />;
  }
  const drop = baseline != null && (baseline - value) >= BASELINE_DROP_THRESHOLD;
  let bg = "bg-emerald-100";
  let text = "text-emerald-800";
  if (drop) {
    bg = "bg-red-100";
    text = "text-red-700";
  } else if (value < 30) {
    bg = "bg-amber-100";
    text = "text-amber-800";
  } else if (value >= 70) {
    bg = "bg-emerald-200";
    text = "text-emerald-900";
  }
  return (
    <span className={`inline-flex h-5 min-w-[28px] items-center justify-center rounded text-[10px] font-medium ${bg} ${text}`}
          title={`${value}%`}>
      {Math.round(value)}
    </span>
  );
}

export default CacheSegment;
